import { useMemo } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';

interface Step {
  /** Step label displayed below the indicator */
  label: string;
  /** Optional description for additional context */
  description?: string;
}

interface StepIndicatorProps {
  /** Array of step definitions */
  steps: Step[];
  /** Current active step (0-indexed) */
  currentStep: number;
  /** Optional override for which steps are marked as completed */
  completedSteps?: number[];
  /** Additional class names for the container */
  className?: string;
  /** If provided, steps become clickable and this callback fires with the clicked step index */
  onStepClick?: (stepIndex: number) => void;
}

type StepStatus = 'completed' | 'current' | 'pending';

function getStepStatus(
  stepIndex: number,
  currentStep: number,
  completedSteps?: number[]
): StepStatus {
  if (completedSteps) {
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  }

  if (stepIndex < currentStep) return 'completed';
  if (stepIndex === currentStep) return 'current';
  return 'pending';
}

function getStepAriaLabel(step: Step, status: StepStatus, stepNumber: number): string {
  const statusText = {
    completed: 'Completed',
    current: 'Current step',
    pending: 'Pending',
  };
  return `Step ${stepNumber}: ${step.label} - ${statusText[status]}`;
}

interface StepCircleProps {
  stepNumber: number;
  status: StepStatus;
}

function StepCircle({ stepNumber, status }: StepCircleProps) {
  return (
    <div
      className={cn(
        'relative flex items-center justify-center',
        'w-10 h-10 rounded-full',
        'font-semibold text-sm',
        'transition-all duration-300 ease-out',
        // Status-specific styles
        {
          // Completed: solid accent green with checkmark
          'bg-accent text-dark-base shadow-sm shadow-accent/30':
            status === 'completed',
          // Current: dark bg with accent ring + glow
          'bg-dark-elevated-2 ring-2 ring-accent text-accent shadow-glow-green':
            status === 'current',
          // Pending: dark outlined
          'bg-dark-elevated-1 border-2 border-border-subtle text-text-muted':
            status === 'pending',
        }
      )}
    >
      {/* Pulse ring for current step */}
      {status === 'current' && (
        <span
          className={cn(
            'absolute inset-0 rounded-full',
            'ring-2 ring-accent/40',
            'animate-ping opacity-75'
          )}
          style={{ animationDuration: '2s' }}
        />
      )}

      {/* Content: checkmark or number */}
      {status === 'completed' ? (
        <CheckIcon className="w-5 h-5" />
      ) : (
        <span className="relative z-10">{stepNumber}</span>
      )}
    </div>
  );
}

interface ConnectorLineProps {
  status: 'completed' | 'active' | 'pending';
}

function ConnectorLine({ status }: ConnectorLineProps) {
  return (
    <div
      className={cn(
        'flex-1 h-0.5 mx-2',
        'transition-all duration-500 ease-out',
        {
          'bg-accent': status === 'completed',
          'bg-gradient-to-r from-accent to-accent/40': status === 'active',
          'bg-border-subtle': status === 'pending',
        }
      )}
    />
  );
}

function StepIndicator({
  steps,
  currentStep,
  completedSteps,
  className,
  onStepClick,
}: StepIndicatorProps) {
  // Memoize step statuses to prevent unnecessary recalculations
  const stepStatuses = useMemo(() => {
    return steps.map((_, index) =>
      getStepStatus(index, currentStep, completedSteps)
    );
  }, [steps, currentStep, completedSteps]);

  // Determine connector line statuses
  const getConnectorStatus = (
    afterIndex: number
  ): 'completed' | 'active' | 'pending' => {
    const leftStatus = stepStatuses[afterIndex];
    const rightStatus = stepStatuses[afterIndex + 1];

    if (leftStatus === 'completed' && rightStatus === 'completed') {
      return 'completed';
    }
    if (leftStatus === 'completed' && rightStatus === 'current') {
      return 'active';
    }
    return 'pending';
  };

  return (
    <nav aria-label="Progress" className={cn('w-full', className)}>
      {/* Desktop/Tablet View */}
      <ol className="hidden sm:flex items-start">
        {steps.map((step, index) => {
          const status = stepStatuses[index];
          const isLast = index === steps.length - 1;

          return (
            <li
              key={index}
              className={cn(
                'flex items-center',
                isLast ? 'flex-shrink-0' : 'flex-1'
              )}
            >
              {/* Step content */}
              <div
                className={cn(
                  'flex flex-col items-center',
                  onStepClick && 'cursor-pointer'
                )}
                onClick={onStepClick ? () => onStepClick(index) : undefined}
                role={onStepClick ? 'button' : undefined}
                aria-label={getStepAriaLabel(step, status, index + 1)}
              >
                <StepCircle stepNumber={index + 1} status={status} />

                {/* Label and description */}
                <div className="mt-3 text-center max-w-[100px]">
                  <p
                    className={cn(
                      'text-sm font-medium truncate',
                      'transition-colors duration-200',
                      status !== 'pending' ? 'text-accent' : 'text-text-muted'
                    )}
                  >
                    {step.label}
                  </p>
                  {step.description && (
                    <p
                      className={cn(
                        'text-xs mt-0.5 truncate',
                        'transition-colors duration-200',
                        status !== 'pending' ? 'text-accent/70' : 'text-text-muted/60'
                      )}
                    >
                      {step.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector line (not after last step) */}
              {!isLast && (
                <div className="flex-1 px-2 pt-5">
                  <ConnectorLine status={getConnectorStatus(index)} />
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Mobile View - Compact horizontal */}
      <div className="sm:hidden">
        {/* Progress bar */}
        <div className="flex items-center mb-3">
          {steps.map((_, index) => {
            const status = stepStatuses[index];
            const isLast = index === steps.length - 1;

            return (
              <div
                key={index}
                className={cn('flex items-center', !isLast && 'flex-1')}
              >
                {/* Small step dot */}
                <div
                  className={cn(
                    'w-3 h-3 rounded-full flex-shrink-0',
                    'transition-all duration-300',
                    onStepClick && 'cursor-pointer',
                    {
                      'bg-accent scale-100': status === 'completed',
                      'bg-accent scale-125 ring-4 ring-accent/20':
                        status === 'current',
                      'bg-border-subtle scale-100': status === 'pending',
                    }
                  )}
                  onClick={onStepClick ? () => onStepClick(index) : undefined}
                  role={onStepClick ? 'button' : undefined}
                  aria-label={onStepClick ? getStepAriaLabel(steps[index], status, index + 1) : undefined}
                />

                {/* Connector */}
                {!isLast && (
                  <div
                    className={cn('flex-1 h-0.5 mx-1', {
                      'bg-accent': getConnectorStatus(index) === 'completed',
                      'bg-gradient-to-r from-accent to-accent/40':
                        getConnectorStatus(index) === 'active',
                      'bg-border-subtle': getConnectorStatus(index) === 'pending',
                    })}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Current step label */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-accent">
            {steps[currentStep]?.label}
          </p>
          <p className="text-xs text-text-muted">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
        {steps[currentStep]?.description && (
          <p className="text-xs text-text-muted/70 mt-0.5">
            {steps[currentStep].description}
          </p>
        )}
      </div>
    </nav>
  );
}

export default StepIndicator;
